import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Building2, Mail, KeyRound, ArrowRight, Loader2 } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

export default function ClientPortalLogin() {
  const navigate = useNavigate();
  const [step, setStep] = useState('email'); // 'email' or 'code'
  const [email, setEmail] = useState('');
  const [accessCode, setAccessCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [devCode, setDevCode] = useState(null);

  const requestAccessCode = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error('Please enter your email address');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/api/client-portal/request-access?email=${encodeURIComponent(email)}`);
      
      // For development, if code is returned directly, show it
      if (response.data.access_code) {
        setDevCode(response.data.access_code);
        toast.info('Access code generated (check below for development mode)');
      } else {
        toast.success(response.data.message);
      }
      setStep('code');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to request access code');
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!accessCode) {
      toast.error('Please enter your access code');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/api/client-portal/login`, {
        email,
        access_code: accessCode
      });
      
      // Store client token
      localStorage.setItem('client_token', response.data.access_token);
      localStorage.setItem('client_data', JSON.stringify(response.data.client));
      
      toast.success('Welcome to the Client Portal!');
      navigate('/client-portal/dashboard');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Invalid access code');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo/Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-r from-emerald-500 to-teal-500 mb-4">
            <Building2 className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-white">Utmost Financial Inc.</h1>
          <p className="text-slate-400 mt-2">Client Portal</p>
        </div>

        <Card className="border-0 shadow-2xl" data-testid="client-login-card">
          <CardHeader className="text-center pb-4">
            <CardTitle className="text-xl">
              {step === 'email' ? 'Access Your Account' : 'Enter Access Code'}
            </CardTitle>
            <CardDescription>
              {step === 'email' 
                ? 'Enter your email to receive an access code'
                : 'Check your email for the 6-character code'
              }
            </CardDescription>
          </CardHeader>
          <CardContent>
            {step === 'email' ? (
              <form onSubmit={requestAccessCode} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email">Email Address</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      id="email"
                      type="email"
                      placeholder="you@example.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="pl-10"
                      data-testid="client-email-input"
                    />
                  </div>
                </div>
                <Button 
                  type="submit" 
                  className="w-full bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700"
                  disabled={loading}
                  data-testid="request-code-btn"
                >
                  {loading ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending...</>
                  ) : (
                    <>Request Access Code <ArrowRight className="w-4 h-4 ml-2" /></>
                  )}
                </Button>
              </form>
            ) : (
              <form onSubmit={handleLogin} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="code">Access Code</Label>
                  <div className="relative">
                    <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      id="code"
                      type="text"
                      placeholder="Enter 6-character code"
                      value={accessCode}
                      onChange={(e) => setAccessCode(e.target.value.toUpperCase())}
                      className="pl-10 uppercase tracking-widest font-mono text-center text-lg"
                      maxLength={6}
                      data-testid="client-code-input"
                    />
                  </div>
                </div>

                {devCode && (
                  <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                    <p className="text-xs text-amber-600 font-medium">Development Mode</p>
                    <p className="text-lg font-mono font-bold text-amber-800 tracking-widest text-center mt-1">
                      {devCode}
                    </p>
                  </div>
                )}

                <Button 
                  type="submit" 
                  className="w-full bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700"
                  disabled={loading}
                  data-testid="client-login-btn"
                >
                  {loading ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Verifying...</>
                  ) : (
                    <>Access Portal <ArrowRight className="w-4 h-4 ml-2" /></>
                  )}
                </Button>

                <Button 
                  type="button" 
                  variant="ghost" 
                  className="w-full text-slate-500"
                  onClick={() => { setStep('email'); setDevCode(null); }}
                >
                  ← Use a different email
                </Button>
              </form>
            )}
          </CardContent>
        </Card>

        <p className="text-center text-slate-500 text-sm mt-6">
          View and manage your invoices securely
        </p>
      </div>
    </div>
  );
}
